import { Link, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Header from "./component/header/Header";
import AppProvider, { AppContext } from "./store/AppProvider";
import { useContext, useEffect } from "react";
import Login from "./pages/login/Login";
import Home from "./pages/home/Home";
import Layout from "./component/layout/Layout";
import Footer from "./component/footer/Footer";
import Callback, { FullPageLoading } from "./component/callback/Callback";
import { ErrorBoundary } from "react-error-boundary";
import GlobalSearch from "./component/globalSearch/GlobalSearch";
import AccountSearch from "./pages/accountSearch/AccountSearch";
import { accountSearchPermissions, catalogPermissions, expiryAssetsPermissions, staticMenuItems } from "./store/staticMenuItems";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { setCatalogSubmenus, setLogosSubmenus } from "./store/reducer/dynamicMenuItemReducer";
import { setStaticSubmenus } from "./store/reducer/staticMenuItemReducer";
import { getCatalog } from "./service/productsService";
import { WarningAmber } from "@mui/icons-material";
import ProductCatalog from "./pages/productCatalog/ProductCatalog";
import LogoCatalog from "./pages/logoCatalog/LogoCatalog";
import ProductPage from "./pages/product/ProductPage";
import ms from "ms";
import useIdle from "./hooks/useIdle";
import LogOutTimer from "./component/logoutTimer/LogoutTimer";
import ScheduleAmendmentsPage from "./pages/contracts/scheduleAmendments/ScheduleAmendmentsPage";
import { useDocumentTitle } from "./hooks/useDocumentTitle";
import ExpiryAssets from "./pages/expiryAssets/ExpiryAssets";
import { REDIRECT_PATH, SESSION_LOGOUT_TIME } from "./utils/constants";
import { getCurrentUser, signInWithRedirect } from "aws-amplify/auth";
import useUser from "./hooks/useUser";
import { handleLogout, hasAccess, languageOptions } from "./utils/Utils";
import { useTranslation } from "react-i18next";
import ViewBulletin from "./pages/salesNews/ViewBulletin";
import BulletinDetail from "./pages/salesNewsVgsap/BulletinDetail";
import BulletinModels from "./pages/salesNewsVgsap/BulletinModels";
import BulletinReport from "./pages/salesNewsVgsap/BulletinReport";
import PromotionDetail from "./pages/salesNewsVgsap/PromotionDetail";
import PromotionEdit from "./pages/salesNewsVgsap/PromotionEdit";
import AdminRouteGuard from "./pages/salesNewsVgsap/AdminRouteGuard";
import { PageLoader } from "./component/loader/PLoader";
import ErrorHandler from "./component/errors/ErrorHandler";
import UnAuthorized from "./component/unAuthorized/UnAuthorized";
import useAuth from "./hooks/useManageTabSession";

// import BulletinDownload from "./pages/salesnews/BulletinDownload";
function App() {
	return (
		<ErrorBoundary fallback={<ErrorFallback />}>
			<AppProvider>
				<PrimaryApp />
			</AppProvider>
		</ErrorBoundary>
	);
}
function AppLayout() {
	const [globalSearch, setGlobalSearch] = useState(false);
	const [sideNav, setSideNav] = useState(false);
	return (
		<>
			<GlobalSearch active={globalSearch} setIsActive={setGlobalSearch} />
			<Header setGlobalSearchState={setGlobalSearch} sideNav={sideNav} setSideNav={setSideNav} />
			<Layout sideNav={sideNav} setSideNav={setSideNav} gbSearch={globalSearch} />
			<Footer />
		</>
	);
}

function filterMenuItems(menuItems, userRole, userCustomerGroup, activeSalesOrg, activeUserType, cognitoRoles) {
	try {
		const filterMenu = menuItems.reduce((filteredMenus, menuItem) => {
			// Checking if top level has permission
			if (menuItem.permissions && !hasAccess(menuItem.permissions, userRole, userCustomerGroup, activeSalesOrg, activeUserType)) {
				return filteredMenus;
			}

			if (menuItem?.subMenus) {
				//Processing subMenus
				const subMenus = Object.keys(menuItem.subMenus).reduce((acc, subMenuObjsKey) => {
					//Forming and filtering subMenus Object
					let subMenuObj = menuItem.subMenus[subMenuObjsKey];

					//Checking TOP level permission on subMenu level
					if (subMenuObj.permissions && !hasAccess(subMenuObj.permissions, userRole, userCustomerGroup, activeSalesOrg, activeUserType)) {
						return acc; // top level doesnt have permission
					} else if (subMenuObj.permissions && subMenuObj.items) {
						const itemObj = Object.keys(subMenuObj.items).reduce((filteredItems, itemObjKey) => {
							let item = subMenuObj.items[itemObjKey];
							//Checking on the last level for permission and lng
							if (hasAccess(item.permissions, userRole, userCustomerGroup, activeSalesOrg, activeUserType)) {
								filteredItems[itemObjKey] = item;
							}
							return filteredItems;
						}, {});
						return { ...acc, [subMenuObjsKey]: { ...subMenuObj, items: itemObj } };
					} else {
						return { ...acc, [subMenuObjsKey]: { ...subMenuObj } };
					}
				}, {});
				return [...filteredMenus, { ...menuItem, subMenus: subMenus }];
			}
			return [...filteredMenus, menuItem];
		}, []);
		return filterMenu;
	} catch (e) {
		console.log("error in filterMenuItems", e);
		return [];
	}
}

function SecurityCheck({ permissions, custom }) {
	const { t } = useTranslation();
	const { activeRoles, activeUserType, activeCustomerGroup, activeSalesOrg, cognitoRoles ,isInternal} = useUser();
	if (!custom && hasAccess(permissions, activeRoles, activeCustomerGroup, activeSalesOrg, activeUserType)) {
		return <Outlet />;
	} else if (custom && hasAccess(permissions, cognitoRoles, activeCustomerGroup, activeSalesOrg, activeUserType, isInternal)) {
		return <Outlet />;
	} else {
		return <ErrorHandler title={"Something went wrong."} desc={"You do not have access to this page."} />
	}
}
function SecuredRoute({ loggedIn }) {
	const loc = useLocation();
	if (loggedIn) return <Outlet />;
	else {
		localStorage.setItem(REDIRECT_PATH, loc.pathname);
		signInWithRedirect();
	}
}
function PublicRoute({ loggedIn }) {
	const loc = useLocation();
	if (!loggedIn) return <Outlet />;
	else {
		return <Navigate to={"/"} replace />;
	}
}
function RouteWithTitle({ title, element }) {
	useDocumentTitle(title);
	return element;
}
function staticRoutes(staticMenuItems, t) {
	return Object.keys(staticMenuItems).map((menuItemName) => {
		const menuItem = staticMenuItems[menuItemName];
		if (!menuItem?.subMenus) {
			return (
				<Route key={menuItemName} element={<SecurityCheck permissions={menuItem?.permissions} />}>
					<Route path={`/${menuItem.route}`} element={<RouteWithTitle title={menuItem.documentTitle} element={menuItem.element} />} />
				</Route>
			);
		} else {
			return Object.keys(menuItem?.subMenus).map((subMenuName) => {
				const subMenuItem = menuItem?.subMenus[subMenuName];
				if (subMenuItem?.route) {
					return (
						<Route key={subMenuName} element={<SecurityCheck permissions={subMenuItem?.permissions} />}>
							<Route
								path={`/${menuItem.route}/${subMenuItem.route}`}
								element={<RouteWithTitle title={subMenuItem.documentTitle} element={subMenuItem.element} />}
							/>
						</Route>
					);
				}
				return <></>;
			});
		}
	});
}
function PrimaryApp() {
	const { setDynamicMenuItems, setStaticFilteredMenuItems } = useContext(AppContext);
	const { t, i18n } = useTranslation();
	const { loginDone } = useAuth();
	const [isLanguageReady, setIsLanguageReady] = useState(false);
	const { activeUser, isLoggedIn, isAuthorized, activeRoles, activeUserType, isInternal,
		activeCustomerGroup, activeSalesOrg, cognitoRoles, dbPermissions } = useUser();
	const queryClient = useQueryClient()
	
	useEffect(() => {
		if (activeUser) {
			queryClient.clear()
		}
	}, [activeUser, queryClient]);

	useEffect(() => {
		if (!activeSalesOrg) {
			setIsLanguageReady(true);
			return;
		}

		const changeLang = async () => {
			const matchedLangKey = Object.keys(languageOptions).find(
				(key) =>
					languageOptions[key]?.salesOrg === activeSalesOrg &&
					languageOptions[key]?.spokenLanguage === "EN"
			);

			if (!matchedLangKey) {
				setIsLanguageReady(true);
				return;
			}

			if (i18n.language !== matchedLangKey) {
				await i18n.changeLanguage(matchedLangKey);
			}
			setIsLanguageReady(true);
		};

		changeLang();
	}, [activeSalesOrg, i18n]);

	useEffect(() => {
		if (i18n?.language && !isLanguageReady) {
			setIsLanguageReady(true);
		}
	}, [i18n.language]);

	const lang = i18n.language;

	const {
		isFetching: isFetchingDynamic,
		data: dynamicItems,
		isError: dynamicMenuError,
	} = useQuery({
		queryKey: ["dynamicItems", lang],
		queryFn: async () => getCatalog(lang),
		enabled: isLoggedIn && isAuthorized && isLanguageReady,
	});

	const {
		isFetching: isFetchingStatic,
		data: staticFilteredItems,
		isError: staticMenuError,
	} = useQuery({
		queryKey: ["staticFilteredItems", lang, dbPermissions],
		queryFn: () => {
			if (!isLoggedIn) return staticMenuItems;
			else if (!isAuthorized) return null;
			else {
				console.log('Debug User Info:', {
					'staticMenuItems ->': staticMenuItems,
					'activeRoles ->': activeRoles,
					'activeCustomerGroup ->': activeCustomerGroup,
					'activeSalesOrg ->': activeSalesOrg,
					'activeUserType ->': activeUserType,
					'cognitoRoles ->': cognitoRoles
				});
				let filteredMenus = filterMenuItems(
					staticMenuItems, activeRoles, activeCustomerGroup,
					activeSalesOrg, activeUserType, cognitoRoles);
				// Filter menu items that require DB-driven permissions
				filteredMenus = filteredMenus.map(menu => {
					if (menu.subMenus) {
						const filteredSubMenus = Object.entries(menu.subMenus).reduce((acc, [key, subMenu]) => {
							const dbPerm = subMenu.permissions?.dbPermission;
							if (dbPerm && !dbPermissions?.[dbPerm]?.hasAccess) {
								return acc; // skip this submenu — user doesn't have DB permission
							}
							acc[key] = subMenu;
							return acc;
						}, {});
						return { ...menu, subMenus: filteredSubMenus };
					}
					// Also check top-level menu items with dbPermission
					const dbPerm = menu.permissions?.dbPermission;
					if (dbPerm && !dbPermissions?.[dbPerm]?.hasAccess) {
						return null; // remove this menu entirely
					}
					return menu;
				}).filter(Boolean);
				console.log("filteredMenus::::::::", filteredMenus);
				return filteredMenus;
			}
		},
	});

	useEffect(() => {
		if (!isFetchingDynamic && dynamicItems) {
			setDynamicMenuItems(setCatalogSubmenus(dynamicItems.subMenu));
			setDynamicMenuItems(setLogosSubmenus(dynamicItems.logos));
			setIsLanguageReady(false);
		}
	}, [dynamicItems]);
	useEffect(() => {
		if (!isFetchingStatic && staticFilteredItems) {
			setStaticFilteredMenuItems(setStaticSubmenus(staticFilteredItems));
		}
	}, [staticFilteredItems]);

	const isIdle = useIdle(isLoggedIn, SESSION_LOGOUT_TIME.WARNING);
	const [showPopUp, setShowPopUp] = useState(isIdle);
	useEffect(() => {
		if (isIdle) {
			setShowPopUp(true);
		}
	}, [isIdle]);

	if ((isLoggedIn && (isFetchingDynamic || isFetchingStatic)) || !loginDone) {
		return (
			<>
				<PageLoader show />
				<AppLayout />
			</>
		);
	}
	if (isLoggedIn && !isAuthorized) {
		return (
			<Routes>
				<Route path='/callback' element={<Callback />} />
				<Route element={<AppLayout />}>
					<Route path='*' element={<UnAuthorized />} />
				</Route>
			</Routes>
		);
	}
	if (dynamicMenuError || staticMenuError) {
		return <ErrorHandler title={"Menu Items not available"} desc={"Not able to load menu items"} />;
	}


	return (
		<>
			<div
				className={showPopUp ? "backdrop backdrop--show" : "backdrop"}
				onClick={() => {
					setShowPopUp(false);
				}}
				style={{ zIndex: "var(--zIndexLogoutPopUp)" }}>
				{showPopUp && <LogOutTimer setOpen={setShowPopUp} />}
			</div>
			<Routes>
				<Route path='/callback' element={<Callback />} />
				<Route element={<PublicRoute loggedIn={isLoggedIn} />}></Route>
				<Route element={<AppLayout />}>
					<Route path='/' element={isLoggedIn ? <Home /> : <Login />} />
					<Route path='/salesNews/viewBulletin' element={<ViewBulletin />} />
					<Route element={<SecuredRoute loggedIn={isLoggedIn} />}>
						<Route element={<SecurityCheck permissions={accountSearchPermissions} custom />}>
							<Route path='/account-search' element={<AccountSearch />} />
						</Route>
						<Route path='/schedule-amendments/:contractNumber' element={<ScheduleAmendmentsPage />} />
						<Route element={<SecurityCheck permissions={catalogPermissions} />}>
							<Route path='/catalog/*' element={<ProductCatalog />} />
							<Route path='/product/*' element={<ProductPage />} />
							<Route path='/logos/*' element={<LogoCatalog />} />
						</Route>
						<Route element={<SecurityCheck permissions={expiryAssetsPermissions} />}>
							<Route path='/expiryAssets' element={<ExpiryAssets />} />
						</Route>
						<Route element={<SecurityCheck permissions={{ roles: [], customerGroups: [], internal: true }} />}>
							<Route path='/sales-news/sales-news-vgsap/detail/:bulletinNo' element={<BulletinDetail />} />
							<Route path='/sales-news/sales-news-vgsap/detail/:bulletinNo/models' element={<BulletinModels />} />
							<Route path='/sales-news/sales-news-vgsap/report' element={<BulletinReport />} />
							<Route path='/sales-news/sales-news-vgsap/promotion/:promoId' element={<AdminRouteGuard><PromotionDetail /></AdminRouteGuard>} />
							<Route path='/sales-news/sales-news-vgsap/promotion/edit/:promoId' element={<AdminRouteGuard><PromotionEdit /></AdminRouteGuard>} />
						</Route>
						{staticFilteredItems && staticRoutes(staticFilteredItems, t)}
					</Route>
					<Route path='*' element={<Page404 />} />
				</Route>
			</Routes>
		</>
	);
}

export function Page404() {
	useDocumentTitle("Page Not Found");
	const { t } = useTranslation();
	return (
		<div className='container' style={{ flexDirection: "column", marginTop: "200px" }}>
			<WarningAmber sx={{ fontSize: 150 }} />
			<h1>{t("404 Not Found")}</h1>
			<p style={{ maxWidth: "50%" }}>
				We apologize for the inconvenience. The page you requested cannot be found. The URL may be incorrect, or the product may have expired or been
				deleted.
			</p>
			<Link to='/'>{t("Return to Home")}</Link>
		</div>
	);
}
function ErrorFallback() {
	useDocumentTitle("Something Went Wrong");
	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}, []);

	return (
		<>
			<div></div>
			<div className='error-fallback'>
				<ErrorHandler title={"Something's wrong here"} desc={"We are working on the fix"} />
			</div>
		</>
	);
}

export default App;
